import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import ReactSelect from "react-select";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import SweetAlert from "sweetalert2";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Insurance name is required"),
  payerId: Yup.string().required("Payer ID is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string()
    .matches(/^\d{10}$/, "Phone number must be 10 digits")
    .required("Phone number is required"),
  shortName: Yup.string(),
  type: Yup.string(),
  fax: Yup.string(),
  address: Yup.string(),
  city: Yup.string(),
  state: Yup.string(),
  zip: Yup.string(),
  id: Yup.number().default(0),
  practiceId: Yup.number().default(0),
});

const InsuranceRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const insuranceId = location.state?.insuranceId || 0; // Retrieve insuranceId from location.state

  const token = localStorage.getItem("Thyra_Token");
  const practiceId = localStorage.getItem("practiceid");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const fields = [
    "id",
    "practiceId",
    "name",
    "shortName",
    "payerId",
    "type",
    "phone",
    "fax",
    "email",
    "address",
    "city",
    "state",
    "zip",
  ];

  const defaultValues = {
    id: 0,
    practiceId: Number(practiceId) || 0,
    payerId: "",
    name: "",
    shortName: "",
    type: "",
    phone: "",
    fax: "",
    email: "",
    address: "",
    city: "",
    state: "",
    zip: "",
  };

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const [submitting, setSubmitting] = useState(false);
  const [isDialogOpenResp, setDialogOpenResp] = useState(false);

  console.log("uzair ahmed insuranceId:", insuranceId); // Debugging

  useEffect(() => {
    if (insuranceId > 0) {
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/Insurances/getInsurancesById?id=${insuranceId}`,
          { headers }
        )
        .then((response) => {
          const data = response; // Adjust based on your API response structure
          const fieldMapping = {
            id: data.id,
            practiceId: data.practiceId,
            name: data.name,
            shortName: data.shortName || "",
            payerId: data.payerId,
            type: data.type || "",
            phone: data.phone,
            fax: data.fax || "",
            email: data.email,
            address: data.address || "",
            city: data.city || "",
            state: data.state || "",
            zip: data.zip || "",
          };

          Object.entries(fieldMapping).forEach(([field, value]) => {
            setValue(field, value);
          });
        })
        .catch((error) => {
          console.error("Error fetching insurance data:", error);
        });
    }
  }, [insuranceId, setValue]);

  const onSubmit = (data) => {
    if (submitting) return;
    setSubmitting(true);

    axios
      .post(`${process.env.REACT_APP_API_URL}/Insurances/AddInsurances`, data, {
        headers,
      })
      .then((response) => {
        if (response.id) {
          setDialogOpenResp(true);
          setTimeout(() => {
            navigate("/insurance");
            setDialogOpenResp(false);
          }, 1000);
        }
        setSubmitting(false);
      })
      .catch((error) => {
        console.error("Submission error:", error.response || error);
        SweetAlert.fire({
          icon: "error",
          title: "Oops...",
          text:
            error.response?.data?.message || "Failed to submit insurance data!",
        });
        setSubmitting(false);
      });
  };

  const handleCancel = () => {
    navigate("/insurance");
  };

  return (
    <Container sx={{ pt: 10, pb: 4, maxWidth: "lg" }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", color: "#2B7B7E" }}>
        Insurance Registration
        </Typography>
      </Box>
      <Dialog
        open={isDialogOpenResp}
        onClose={() => setDialogOpenResp(false)}
        PaperProps={{
          style: {
            marginRight: "5px",
            float: "right",
            marginTop: "5px",
            color: "red",
            cursor: "pointer",
          },
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: "8px",
            width: "500px",
          }}
        >
          <CheckCircleIcon style={{ fontSize: "100px", color: "green" }} />
          <h5
            style={{
              textAlign: "center",
              fontSize: "16px",
              fontWeight: "normal",
              color: "#444444",
            }}
          >
            Insurance {insuranceId > 0 ? "updated" : "added"} successfully
          </h5>
          <DialogContent />
        </div>
      </Dialog>
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          borderRadius: "12px",
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
          p: 4,
        }}
      >
        <div className="row">
          <div className="col-12 mb-3">
            <Box sx={{ borderBottom: 1, borderColor: "divider" }} />
            <div className="row">
              <div className="col-md-3">
                <Typography component="label" color="black">
                  Name
                </Typography>
                <TextField
                  {...register("name")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter your Insurance Name"
                  style={{ marginTop: "8px" }}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </div>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  Payer ID
                </Typography>
                <TextField
                  {...register("payerId")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter your Payer ID"
                  style={{ marginTop: "8px" }}
                  error={!!errors.payerId}
                  helperText={errors.payerId?.message}
                />
              </div>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  Phone No
                </Typography>
                <TextField
                  {...register("phone")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter your Phone No"
                  style={{ marginTop: "8px" }}
                  onInput={(e) => {
                    const input = e.target;
                    input.value = input.value.replace(/\D/g, "").slice(0, 10);
                  }}
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                  }}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                />
              </div>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  Fax No
                </Typography>
                <TextField
                  {...register("fax")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter your Fax No"
                  style={{ marginTop: "8px" }}
                />
              </div>
            </div>
            <div className="row" style={{ marginTop: "10px" }}>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  Email
                </Typography>
                <TextField
                  {...register("email")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter your Email"
                  style={{ marginTop: "8px" }}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              </div>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  Address
                </Typography>
                <TextField
                  {...register("address")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter your Address"
                  style={{ marginTop: "8px" }}
                />
              </div>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  City
                </Typography>
                <TextField
                  {...register("city")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter your City"
                  style={{ marginTop: "8px" }}
                />
              </div>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  State
                </Typography>
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <ReactSelect
                      {...field}
                      options={[
                        { value: "AL", label: "ALABAMA" },
                        { value: "AK", label: "ALASKA" },
                        { value: "AS", label: "AMERICAN SAMOA" },
                        { value: "AZ", label: "ARIZONA" },
                      ]}
                      value={
                        getValues("state")
                          ? {
                              label: getValues("state"),
                              value: getValues("state"),
                            }
                          : null
                      }
                      onChange={(selectedOption) =>
                        setValue("state", selectedOption?.value || "")
                      }
                      styles={{
                        control: (base) => ({
                          ...base,
                          marginTop: 8,
                          marginBottom: 0,
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 5000,
                        }),
                      }}
                    />
                  )}
                />
              </div>
            </div>
            <div className="row" style={{ marginTop: "10px" }}>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  Zip Code
                </Typography>
                <TextField
                  {...register("zip")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter your Zip Code"
                  style={{ marginTop: "8px" }}
                  onInput={(e) => {
                    const input = e.target;
                    input.value = input.value.replace(/\D/g, "").slice(0, 9);
                  }}
                />
              </div>
            </div>
            <Grid
              container
              spacing={2}
              style={{ margin: 20, width: "90%", justifyContent: "flex-end" }}
            >
               <Button
                onClick={handleCancel}
                sx={{
                  width: "100px",
                  height: "35px",
                  backgroundColor: "#2B7B7E",
                  color: "white",
                  borderRadius: "80px",
                  fontSize: "14px",
                  "&:hover": { backgroundColor: "#1a1a1a" },
                }}
              >
                <CancelOutlinedIcon
                  fontSize="medium"
                  style={{ color: "red", paddingRight: "5px" }}
                />
                Cancel
              </Button>
              <Button
                onClick={handleSubmit(onSubmit)}
                disabled={submitting}
                sx={{
                  width: "100px",
                  height: "35px",
                  backgroundColor: "#2B7B7E",
                  color: "white",
                  borderRadius: "80px",
                  fontSize: "14px",
                  "&:hover": { backgroundColor: "#1a1a1a" },
                }}
              >
                <SaveOutlinedIcon
                  fontSize="medium"
                  style={{
                    color: "white",
                    paddingRight: "5px",

                    paddingBottom: "2px",
                  }}
                />
                Save
              </Button>
             
            </Grid>
          </div>
        </div>
      </Box>
    </Container>
  );
};

export default InsuranceRegistration;
